// src/routes/waypoint/scenarios.tsx

import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Briefcase, CheckCircle, Clock, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useCreateWaypointSession } from "@/hooks/use-waypoint-sessions";

type Difficulty = "foundation" | "standard" | "stretch";

interface ScenarioCard {
  slug: string;
  name: string;
  description: string;
  tags: string[];
  personas: number;
  durationMin: number;
  recommended?: boolean;
}

const SCENARIOS: ScenarioCard[] = [
  {
    slug: "scope-bomb",
    name: "Scope Bomb",
    description:
      "A stakeholder requests a major scope addition mid-project. Protect the timeline without losing the relationship.",
    tags: ["Objection Navigation", "Stakeholder Orchestration"],
    personas: 3,
    durationMin: 8,
    recommended: true,
  },
  {
    slug: "budget-freeze",
    name: "Budget Freeze",
    description:
      "Finance has frozen discretionary spend. Keep your deal alive through creative structuring and value articulation.",
    tags: ["Value-Led Selling", "Commercial Acumen"],
    personas: 2,
    durationMin: 10,
  },
  {
    slug: "champion-gone",
    name: "Champion Gone",
    description:
      "Your internal sponsor just left. Re-establish trust and momentum with a skeptical new decision-maker.",
    tags: ["Consultative Partnership", "Stakeholder Orchestration"],
    personas: 2,
    durationMin: 10,
  },
  {
    slug: "committee-call",
    name: "Committee Call",
    description:
      "Five stakeholders, five agendas. Navigate competing priorities and land a clear decision.",
    tags: ["Stakeholder Orchestration", "Commercial Acumen"],
    personas: 4,
    durationMin: 15,
  },
  {
    slug: "competitor-swap",
    name: "Competitor Swap",
    description:
      "A customer is considering switching vendors at renewal. Defend the relationship on value, not price.",
    tags: ["Value-Led Selling", "Objection Navigation"],
    personas: 2,
    durationMin: 8,
  },
  {
    slug: "executive-sponsor",
    name: "Executive Sponsor",
    description:
      "A C-suite sponsor joins the call unannounced. Pivot to executive-level conversation without losing momentum.",
    tags: ["Consultative Partnership", "Commercial Acumen"],
    personas: 3,
    durationMin: 12,
  },
];

const DIFFICULTY_OPTIONS: { value: Difficulty; label: string; description: string }[] = [
  {
    value: "foundation",
    label: "Foundation",
    description: "Cooperative room, predictable objections",
  },
  { value: "standard", label: "Standard", description: "Balanced challenge, realistic dynamics" },
  { value: "stretch", label: "Stretch", description: "Hostile room, curveball objections" },
];

// Avatar colors for scenario icons
const ICON_COLORS = ["#2D9BD6", "#407EC1", "#2EA86A", "#E8A93C", "#9B5DE5", "#D8443C"];

export function ScenariosPage() {
  const navigate = useNavigate();
  const create = useCreateWaypointSession();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty>("standard");

  const selectedScenario = SCENARIOS.find((s) => s.slug === selectedSlug);

  async function handleStart() {
    if (!selectedSlug) return;
    // Only "scope-bomb" is implemented; show coming-soon for others
    if (selectedSlug !== "scope-bomb") {
      toast.info("This scenario is coming soon. Try Scope Bomb for now!");
      return;
    }
    try {
      const session = await create.mutateAsync({ scenarioSlug: selectedSlug, difficulty });
      await navigate({ to: "/dashboard/waypoint/$sessionId", params: { sessionId: session.id } });
    } catch {
      toast.error("Failed to start session. Please try again.");
    }
  }

  return (
    <div style={{ animation: "wp-fade 0.3s ease-out", paddingBottom: selectedSlug ? 100 : 0 }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            color: "#407EC1",
            marginBottom: 6,
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          Choose your moment
        </p>
        <h1
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: 30,
            color: "#001847",
            margin: "0 0 8px",
          }}
        >
          Scenarios
        </h1>
        <p style={{ fontSize: 15, color: "#64748b", margin: 0, lineHeight: 1.5 }}>
          Six high-stakes rooms. Pick one, set the difficulty, and you're in.
        </p>
      </div>

      {/* Scenario grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
        }}
      >
        {SCENARIOS.map((scenario, i) => {
          const isSelected = selectedSlug === scenario.slug;
          const iconColor = ICON_COLORS[i % ICON_COLORS.length];
          const isLive = scenario.slug === "scope-bomb";

          return (
            <button
              type="button"
              key={scenario.slug}
              onClick={() => setSelectedSlug(isSelected ? null : scenario.slug)}
              style={{
                background: "#fff",
                border: `2px solid ${isSelected ? "#1E6CC7" : "#e2e8f0"}`,
                borderRadius: 14,
                padding: 20,
                cursor: "pointer",
                textAlign: "left",
                transition: "border-color 0.15s, box-shadow 0.15s",
                boxShadow: isSelected
                  ? "0 10px 26px rgba(30,108,199,0.20)"
                  : "0 1px 3px rgba(0,0,0,0.04)",
                position: "relative",
                opacity: !isLive ? 0.65 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isSelected && isLive) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#7FC2EC";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    "0 4px 12px rgba(30,108,199,0.10)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    "0 1px 3px rgba(0,0,0,0.04)";
                }
              }}
            >
              {scenario.recommended && (
                <span
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    background: "#001847",
                    color: "#7FC2EC",
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    padding: "3px 8px",
                    borderRadius: 999,
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  For you
                </span>
              )}

              {!isLive && (
                <span
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    background: "#f0f4fb",
                    color: "#94a3b8",
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    padding: "3px 8px",
                    borderRadius: 999,
                  }}
                >
                  Coming soon
                </span>
              )}

              {/* Icon */}
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "#E8F2FB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                }}
              >
                <Briefcase style={{ width: 18, height: 18, color: iconColor }} />
              </div>

              <h3
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#001847",
                  margin: "0 0 8px",
                }}
              >
                {scenario.name}
              </h3>
              <p
                style={{
                  fontSize: 12.5,
                  color: "#64748b",
                  lineHeight: 1.55,
                  margin: "0 0 14px",
                }}
              >
                {scenario.description}
              </p>

              {/* Tags */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {scenario.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: "#E8F2FB",
                      color: "#1E6CC7",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "3px 8px",
                      borderRadius: 999,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Meta */}
              <div style={{ display: "flex", gap: 14 }}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 11,
                    color: "#94a3b8",
                  }}
                >
                  <Users style={{ width: 12, height: 12 }} />
                  {scenario.personas} in room
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 11,
                    color: "#94a3b8",
                  }}
                >
                  <Clock style={{ width: 12, height: 12 }} />~{scenario.durationMin} min
                </span>
              </div>

              {isSelected && (
                <div
                  style={{
                    position: "absolute",
                    top: 14,
                    right: scenario.recommended ? 80 : 14,
                  }}
                >
                  <CheckCircle style={{ width: 18, height: 18, color: "#1E6CC7" }} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Sticky bottom bar */}
      {selectedScenario && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 232,
            right: 0,
            background: "#001847",
            borderTop: "1px solid rgba(127,194,236,0.15)",
            padding: "16px 40px",
            display: "flex",
            alignItems: "center",
            gap: 20,
            zIndex: 50,
            animation: "wp-fade 0.2s ease-out",
          }}
        >
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: "#FAFBFD",
                margin: "0 0 2px",
              }}
            >
              {selectedScenario.name}
            </p>
            <p style={{ fontSize: 12, color: "rgba(230,238,248,0.6)", margin: 0 }}>
              Choose a difficulty level
            </p>
          </div>

          {/* Difficulty toggle */}
          <div
            style={{
              display: "flex",
              gap: 4,
              background: "rgba(255,255,255,0.07)",
              borderRadius: 8,
              padding: 4,
            }}
          >
            {DIFFICULTY_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => setDifficulty(opt.value)}
                style={{
                  padding: "7px 16px",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  transition: "background 0.15s, color 0.15s",
                  background: difficulty === opt.value ? "#1E6CC7" : "transparent",
                  color: difficulty === opt.value ? "#fff" : "rgba(230,238,248,0.6)",
                }}
                title={opt.description}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <button
            type="button"
            onClick={handleStart}
            disabled={create.isPending}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 24px",
              background: create.isPending ? "rgba(30,108,199,0.5)" : "#1E6CC7",
              border: "none",
              borderRadius: 10,
              cursor: create.isPending ? "not-allowed" : "pointer",
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              transition: "background 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {create.isPending ? "Starting…" : "Read the brief"}
            {!create.isPending && <ArrowRight style={{ width: 16, height: 16 }} />}
          </button>
        </div>
      )}
    </div>
  );
}
